import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getPriceBoard } from "@/lib/prices.functions";
import { formatMoney, type PriceRow } from "@/lib/prices-shared";
import { CardImage } from "@/components/CardImage";
import { RarityBadge } from "@/components/RarityBadge";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, howToSchema, toolSchema } from "@/lib/schema";
import { InternalLinkBlock } from "@/components/InternalLinkBlock";
import { cardLinkProps } from "@/lib/card-link";

const URL = "https://palstars.com/card-value-calculator";
const TITLE = "Palworld TCG Value Calculator — Card Worth";
const DESCRIPTION =
  "Free Palworld TCG value calculator: add the cards you own, set condition and grade, and get your total collection value from live asking prices — no sign-up needed.";

/** Condition multipliers applied to a raw market price. */
const CONDITIONS = [
  { id: "NM", label: "Near Mint", mult: 1 },
  { id: "LP", label: "Lightly Played", mult: 0.85 },
  { id: "MP", label: "Moderately Played", mult: 0.65 },
  { id: "HP", label: "Heavily Played", mult: 0.45 },
  { id: "DMG", label: "Damaged", mult: 0.3 },
] as const;

/** Certified-grade multipliers, relative to a Near Mint raw copy. */
const GRADES = [
  { id: "raw", label: "Raw (ungraded)", mult: 1 },
  { id: "psa8", label: "PSA / CGC 8", mult: 1.3 },
  { id: "psa9", label: "PSA / CGC 9", mult: 1.9 },
  { id: "bgs95", label: "BGS 9.5", mult: 3.1 },
  { id: "psa10", label: "PSA 10 / CGC 10", mult: 4.2 },
] as const;

type ConditionId = (typeof CONDITIONS)[number]["id"];
type GradeId = (typeof GRADES)[number]["id"];

interface Holding {
  code: string;
  qty: number;
  condition: ConditionId;
  grade: GradeId;
}

const STORAGE_KEY = "palstars.value-calculator.v1";

const STEPS = [
  { name: "Search your cards", text: "Type a Pal name or the printed card code (for example EBP01-025SSP) and add each card you own." },
  { name: "Set quantity, condition and grade", text: "Enter how many copies you have, pick Near Mint through Damaged, and mark any slabbed copies with their grade." },
  { name: "Use the live pricing basis", text: "Value each card from the current validated English asking market." },
  { name: "Read your collection value", text: "The calculator totals every line, shows your biggest holdings and the share of value locked in chase cards." },
];

const FAQ: FaqItem[] = [
  {
    q: "How much is my Palworld TCG card worth?",
    a: "Add the card by name or printed code above and this calculator returns its live value from the Pal Stars price feed. A Near Mint raw copy is valued at the blended market price; played copies are discounted (Lightly Played about 85%, Moderately Played 65%, Heavily Played 45%, Damaged 30%) and graded slabs carry a premium of roughly 1.3x at grade 8 up to about 4.2x at PSA 10.",
  },
  {
    q: "Is the Palworld TCG value calculator free?",
    a: "Yes. It is free, needs no account and runs in your browser — your card list is stored locally on your own device. If you want buy price, sell price and profit tracked across time, the Pal Stars collection tracker does that for signed-in members.",
  },
  {
    q: "Which Palworld cards hold the most value?",
    a: "Value concentrates in the chase rarities: SSS, SSP, OSR and SP printings, plus the not-for-sale EPR and ESOUL promos. Commons and uncommons are worth cents each, so a large binder can still hold most of its value in three or four cards — the calculator shows that concentration directly.",
  },
  {
    q: "How often do the prices update?",
    a: "The underlying price board syncs once a day from validated marketplace data, with outliers rejected before a price is published.",
  },
];

export const Route = createFileRoute("/card-value-calculator")({
  // Server-side render from the real price board so first paint (crawler and
  // browser alike) matches the hydrated client render.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["price-board"],
      queryFn: () => getPriceBoard(),
      staleTime: 5 * 60 * 1000,
    }),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Palworld TCG value calculator — what is my collection worth?" },
      { property: "og:description", content: "Add your cards, set condition and grade, and get a live total collection value from validated asking prices." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([["Home", "/"], ["Price Chart", "/prices"], ["Value Calculator", "/card-value-calculator"]]),
      toolSchema({ name: "Palworld TCG Value Calculator", description: DESCRIPTION, path: "/card-value-calculator" }),
      howToSchema({
        name: "How to value a Palworld TCG collection",
        description: "Add your cards, set condition and grade, and read your total collection value from live asking prices.",
        path: "/card-value-calculator",
        steps: STEPS,
      }),
      faqSchema(FAQ, URL),
    ],
  }),
  component: ValueCalculatorPage,
});

const condMult = (id: ConditionId) => CONDITIONS.find((c) => c.id === id)!.mult;
const gradeMult = (id: GradeId) => GRADES.find((g) => g.id === id)!.mult;

function ValueCalculatorPage() {
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    staleTime: 5 * 60 * 1000,
  });

  const rows = data?.rows ?? [];
  const byCode = useMemo(() => {
    const m = new Map<string, PriceRow>();
    for (const r of rows) m.set(r.code, r);
    return m;
  }, [rows]);

  const [holdings, setHoldings] = useState<Holding[]>([]);
  const [query, setQuery] = useState("");

  // Restore the local card list (device-only, no account required).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setHoldings(JSON.parse(raw) as Holding[]);
    } catch {
      /* ignore malformed local state */
    }
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(holdings));
    } catch {
      /* storage unavailable */
    }
  }, [holdings]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return rows
      .filter((r) => r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q))
      .slice(0, 8);
  }, [query, rows]);

  const add = (code: string) => {
    setHoldings((prev) =>
      prev.some((h) => h.code === code)
        ? prev.map((h) => (h.code === code ? { ...h, qty: h.qty + 1 } : h))
        : [...prev, { code, qty: 1, condition: "NM", grade: "raw" }],
    );
    setQuery("");
  };
  const update = (code: string, patch: Partial<Holding>) =>
    setHoldings((prev) => prev.map((h) => (h.code === code ? { ...h, ...patch } : h)));
  const remove = (code: string) => setHoldings((prev) => prev.filter((h) => h.code !== code));

  const lines = useMemo(
    () =>
      holdings.flatMap((h) => {
        const row = byCode.get(h.code);
        if (!row) return [];
        const unit = row.market * condMult(h.condition) * gradeMult(h.grade);
        return [{ ...h, row, unit, total: unit * h.qty }];
      }),
    [holdings, byCode],
  );

  const totalCards = lines.reduce((a, l) => a + l.qty, 0);
  const totalValue = lines.reduce((a, l) => a + l.total, 0);
  const chaseValue = lines
    .filter((l) => ["SSS", "SSP", "OSR", "SP", "TSP", "PR"].includes(l.row.rarity))
    .reduce((a, l) => a + l.total, 0);
  const top = [...lines].sort((a, b) => b.total - a.total).slice(0, 3);

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-10 space-y-8">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Pal Stars · TCG tool</p>
        <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl">Palworld TCG Value Calculator</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-muted-foreground">
          Add the Palworld OFFICIAL CARD GAME cards you own, set the quantity, condition and grade of each one, and this
          free calculator returns what your collection is worth right now. Values come from the live Pal Stars price
           feed. Every line uses the validated English asking market and is discounted for condition
          (Near Mint 100% down to Damaged 30%) or multiplied for a certified grade (about 1.9x at PSA 9 and 4.2x at PSA
          10). Nothing is stored on our servers — your list stays on your device.
        </p>
      </header>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Add your cards</h2>
        <label className="mt-3 block text-sm">
          <span className="block text-muted-foreground">Search by Pal name or printed code</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cattiva, Anubis, EBP01-025SSP…"
            className="mt-1 w-full rounded-lg border border-border/60 bg-background px-3 py-2"
            aria-label="Search Palworld TCG cards to add"
          />
        </label>
        {matches.length > 0 && (
          <ul className="mt-2 divide-y divide-border/50 rounded-lg border border-border/60">
            {matches.map((r) => (
              <li key={r.code}>
                <button
                  onClick={() => add(r.code)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-card"
                >
                  <CardImage src={r.image} alt={`${r.name} Palworld TCG card ${r.code}`} width={34} height={47} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{r.name}</span>
                    <span className="block text-xs text-muted-foreground">{r.code}</span>
                  </span>
                  <RarityBadge code={r.rarity} />
                  <span className="tabular-nums text-sm">{formatMoney(r.market, "USD")}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

      </section>

      <section className="grid gap-3 sm:grid-cols-3">
        {[
          { label: "Cards valued", value: totalCards.toLocaleString(), sub: `${lines.length} unique cards` },
          {
             label: "Collection value (asking)",
            value: formatMoney(totalValue, "USD"),
             sub: "Validated English listings",
          },
          {
            label: "Held in chase cards",
            value: formatMoney(chaseValue, "USD"),
            sub: totalValue > 0 ? `${Math.round((chaseValue / totalValue) * 100)}% of total value` : "—",
          },
        ].map((k) => (
          <div key={k.label} className="glass rounded-2xl p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">{k.label}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums">{k.value}</p>
            <p className="text-xs text-muted-foreground tabular-nums">{k.sub}</p>
          </div>
        ))}
      </section>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Your cards</h2>
        {lines.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            No cards added yet. Search above, or browse the{" "}
            <Link to="/cards" className="font-semibold text-primary hover:underline">
              Palworld card database
            </Link>{" "}
            to find the printed codes on your cards.
          </p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <caption className="sr-only">Your Palworld TCG cards and their calculated values</caption>
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th scope="col" className="py-2">Card</th>
                  <th scope="col" className="py-2">Qty</th>
                  <th scope="col" className="py-2">Condition</th>
                  <th scope="col" className="py-2">Grade</th>
                  <th scope="col" className="py-2">Unit value</th>
                  <th scope="col" className="py-2">Line value</th>
                  <th scope="col" className="py-2"><span className="sr-only">Remove</span></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.code} className="border-t border-border/50">
                    <th scope="row" className="py-2 text-left font-semibold">
                      <span className="flex items-center gap-2">
                        <CardImage src={l.row.image} alt={`${l.row.name} Palworld TCG card ${l.code}`} width={30} height={42} />
                        <span>
                          <Link {...cardLinkProps(l.code)} className="hover:underline">
                            {l.row.name}
                          </Link>
                          <span className="block text-xs font-normal text-muted-foreground">{l.code} · {l.row.rarity}</span>
                        </span>
                      </span>
                    </th>
                    <td className="py-2">
                      <input
                        type="number"
                        min={1}
                        max={999}
                        value={l.qty}
                        aria-label={`Quantity of ${l.row.name}`}
                        onChange={(e) => update(l.code, { qty: Math.max(1, Math.min(999, Number(e.target.value) || 1)) })}
                        className="w-16 rounded border border-border/60 bg-background px-2 py-1 tabular-nums"
                      />
                    </td>
                    <td className="py-2">
                      <select
                        value={l.condition}
                        aria-label={`Condition of ${l.row.name}`}
                        onChange={(e) => update(l.code, { condition: e.target.value as ConditionId })}
                        className="rounded border border-border/60 bg-background px-2 py-1"
                      >
                        {CONDITIONS.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2">
                      <select
                        value={l.grade}
                        aria-label={`Grade of ${l.row.name}`}
                        onChange={(e) => update(l.code, { grade: e.target.value as GradeId })}
                        className="rounded border border-border/60 bg-background px-2 py-1"
                      >
                        {GRADES.map((g) => (
                          <option key={g.id} value={g.id}>{g.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 tabular-nums">{formatMoney(l.unit, "USD")}</td>
                    <td className="py-2 tabular-nums font-semibold">{formatMoney(l.total, "USD")}</td>
                    <td className="py-2">
                      <button onClick={() => remove(l.code)} className="rounded px-2 py-1 text-xs hover:bg-card" aria-label={`Remove ${l.row.name}`}>
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {top.length > 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            Biggest holdings: {top.map((t) => `${t.row.name} (${formatMoney(t.total, "USD")})`).join(", ")}. Condition and
            grade multipliers are market conventions, not a certified appraisal — always check the live sold prices before
            buying or selling.
          </p>
        )}
      </section>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">How to value your collection</h2>
        <ol className="mt-3 space-y-3">
          {STEPS.map((s, i) => (
            <li key={s.name} className="rounded-lg border border-border/60 p-3">
              <p className="text-sm font-semibold">{i + 1}. {s.name}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="glass rounded-2xl p-5">
        <h2 className="text-xl font-bold">Next steps</h2>
        <ul className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
          <li><Link to="/prices" className="font-semibold text-primary hover:underline">Palworld TCG price chart →</Link> per-card asking prices.</li>
          <li><Link to="/collection" className="font-semibold text-primary hover:underline">Collection tracker →</Link> buy price, sell price and profit.</li>
          <li><Link to="/pack-calculator" className="font-semibold text-primary hover:underline">Pack calculator →</Link> cost per chase card.</li>
          <li><Link to="/guides/$slug" params={{ slug: "palworld-card-rarity" }} className="font-semibold text-primary hover:underline">Card rarity guide →</Link> what SSP, OSR and SSS mean.</li>
        </ul>
      </section>

      <InternalLinkBlock path="/card-value-calculator" />
      <FaqSection items={FAQ} heading="Palworld TCG value calculator FAQ" />
    </div>
  );
}
